// Orkestrator E3 §4 — provider policy in the pickers: `forbidden` options are hidden by the caller
// (`policyHides`), `gray` ones carry this dated badge (tooltip = the vendor note, link = the page).
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ShieldAlert } from 'lucide-react'
import { api } from '#/lib/api'
import { policyFor, policyWarns, providerFor, seedTable, type PolicyCell, type PolicyScenario, type PolicyTable } from '#/lib/agents/policy'
import { fmtDate } from '#/lib/format'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'

/** Public table (cached an hour server-side). The seed is the placeholder, so SSR and the first paint agree. */
export function useProviderPolicy() {
  return useQuery({ queryKey: ['provider-policy'], queryFn: () => api<PolicyTable>('/api/provider-policy'), staleTime: 60 * 60_000, placeholderData: seedTable(), retry: false })
}

/** The cell governing one option (scenario + agent / API-key vendor). */
export function usePolicyCell(x: { scenario: PolicyScenario; agent?: string | null; connectionProvider?: string | null }): PolicyCell | null {
  const table = useProviderPolicy().data ?? seedTable()
  return policyFor(providerFor(x), x.scenario, table)
}

/** A policy date (YYYY-MM-DD) in the reader's locale; noon UTC so no timezone moves the day. */
export const policyDate = (d: string | null | undefined) => (d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? fmtDate(`${d}T12:00:00Z`) : '—')

export function PolicyBadge({ cell, className }: { cell: PolicyCell | null | undefined; className?: string }) {
  if (!cell || !policyWarns(cell)) return null
  return (
    <Link
      to="/kebijakan-penyedia"
      title={cell.note ?? undefined}
      className={cn('inline-flex max-w-full items-center gap-1 rounded-full border border-status-blocked/30 bg-status-blocked/5 px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:text-foreground', className)}
      data-testid="policy-badge"
      data-status={cell.status}
    >
      <ShieldAlert className="size-3 shrink-0 text-status-blocked" aria-hidden />
      <span className="truncate">{m.pol_badge_gray({ date: policyDate(cell.verifiedAt) })}</span>
    </Link>
  )
}
